import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Alert } from 'common-src/components/base';
import { apiRequest, getRestErrorMessage } from 'common-src/features/rest';
import { openDocument } from 'common-src/models/PatientDocument';

import { MessageBody } from 'src/components/elements';
import useAlert from 'src/hooks/useAlert';
import { getNormalizedThreadMessages, sendSecureMessage } from 'src/utils/secureMessagingUtils';

import { BriefRow } from '../Visit';
import styles from './TaskConversation.module.scss';

const getMessageSenderLabel = (message) => {
  if (!message?.senderDisplayName) return '';

  return message.senderDisplayName;
};

const getConversationHeaderTime = (sentAt) => {
  if (!sentAt) return '';

  const parsed = moment(sentAt, 'MM/DD/YYYY HH:mm', true);
  if (!parsed.isValid()) return sentAt;

  return parsed.format('MMM D, h:mm A');
};

const TaskConversation = ({ task, canReply = true, onReplySent = undefined }) => {
  const { showAlert, AlertType } = useAlert();
  const [conversationMessages, setConversationMessages] = useState([]);
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  const [conversationError, setConversationError] = useState('');
  const [replyText, setReplyText] = useState('');
  const [replyAttachment, setReplyAttachment] = useState(null);
  const [isSendingReply, setIsSendingReply] = useState(false);
  const replyAttachmentInputRef = useRef(null);

  const resetReplyState = () => {
    setReplyText('');
    setReplyAttachment(null);
    if (replyAttachmentInputRef.current) {
      replyAttachmentInputRef.current.value = '';
    }
  };

  const fetchThreadMessages = async (threadId) => {
    const response = await apiRequest({
      endpoint: `patientPortalMessages/thread/${threadId}`,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(getRestErrorMessage(err) || 'Failed to fetch conversation');
    }

    const data = await response.json();
    const payload =
      data?.PatientPortalMessage ??
      data?.patientPortalMessage ??
      data?.patient_portal_message ??
      [];
    const items = Array.isArray(payload) ? payload : payload ? [payload] : [];
    const normalizedMessages = getNormalizedThreadMessages(items);

    setConversationMessages(normalizedMessages);
    setConversationError('');
    return normalizedMessages;
  };

  const onDownloadAttachment = async (attachmentId) => {
    if (!attachmentId) return;

    try {
      let fileName = `attachment-${attachmentId}`;
      const response = await apiRequest({ endpoint: `patientDocuments/${attachmentId}` });
      if (response.ok) {
        const data = await response.json();
        fileName =
          data?.PatientDocument?.fileName ??
          data?.patientDocument?.fileName ??
          data?.patient_document?.file_name ??
          fileName;
      }

      await openDocument(attachmentId, fileName, (error) => {
        showAlert(AlertType.Error, '', error.message || 'Failed to download attachment');
      });
    } catch (error) {
      showAlert(AlertType.Error, '', error.message || 'Failed to download attachment');
    }
  };

  const onSendReply = async () => {
    const rootMessage = conversationMessages[0];

    if (!task?.threadId) {
      showAlert(AlertType.Error, '', 'Thread not found');
      return;
    }

    if (!replyText.trim()) return;

    if (!rootMessage?.receiverType?.length || !rootMessage?.notificationType) {
      showAlert(AlertType.Error, '', 'Missing thread metadata needed to send reply');
      return;
    }

    setIsSendingReply(true);

    try {
      await sendSecureMessage({
        patientId: task.patientId,
        messageSubject: rootMessage?.messageSubject || task.title,
        body: replyText.trim(),
        sendToPatient: !!rootMessage?.sendToPatient,
        sendToCaregiver: !!rootMessage?.sendToCaregiver,
        receiverType: rootMessage.receiverType,
        notificationType: rootMessage.notificationType,
        attachment: replyAttachment,
        threadId: task.threadId,
      });

      await fetchThreadMessages(task.threadId);
      await onReplySent?.();
      resetReplyState();
      showAlert(AlertType.Success, '', 'Message sent successfully!');
    } catch (error) {
      showAlert(AlertType.Error, '', error.message || 'Failed to send message');
    } finally {
      setIsSendingReply(false);
    }
  };

  useEffect(() => {
    if (!task?.threadId) {
      setConversationMessages([]);
      setConversationError('');
      resetReplyState();
      return;
    }

    let cancelled = false;

    const loadConversation = async () => {
      setIsConversationLoading(true);
      try {
        const messages = await fetchThreadMessages(task.threadId);
        if (cancelled) return;
        setConversationMessages(messages);
      } catch (error) {
        if (cancelled) return;
        setConversationMessages([]);
        setConversationError(error.message || 'Failed to fetch conversation');
      } finally {
        if (!cancelled) {
          setIsConversationLoading(false);
        }
      }
    };

    loadConversation();

    return () => {
      cancelled = true;
    };
  }, [task?.id, task?.threadId]);

  return (
    <BriefRow
      label="Conversation"
      meta={
        conversationMessages.length
          ? `${conversationMessages.length} ${conversationMessages.length === 1 ? 'message' : 'messages'}`
          : undefined
      }
    >
      {!task?.threadId && (
        <div className={styles.stateCard}>This task does not have a linked portal thread yet.</div>
      )}

      {!!task?.threadId && (
        <>
          <div className={styles.conversationCard}>
            {isConversationLoading && (
              <div className={styles.stateCard}>Loading conversation...</div>
            )}
            {!isConversationLoading && !!conversationError && (
              <div className={styles.stateCard}>{conversationError}</div>
            )}
            {!isConversationLoading && !conversationError && !conversationMessages.length && (
              <div className={styles.stateCard}>No messages found for this thread.</div>
            )}
            {!isConversationLoading &&
              !conversationError &&
              conversationMessages.map((message) => {
                const isClinic = `${message.senderType}`.toUpperCase() === 'CLINIC';
                const senderLabel = getMessageSenderLabel(message);

                return (
                  <div
                    key={message.id || `${message.sentAt}-${message.body}`}
                    className={[
                      styles.bubbleRow,
                      isClinic ? styles.bubbleRowRight : styles.bubbleRowLeft,
                    ].join(' ')}
                  >
                    <div
                      className={[
                        styles.bubbleContainer,
                        isClinic ? styles.bubbleContainerRight : styles.bubbleContainerLeft,
                      ].join(' ')}
                    >
                      <div
                        className={[
                          styles.bubble,
                          isClinic ? styles.bubbleClinic : styles.bubbleOther,
                        ].join(' ')}
                      >
                        {!!senderLabel && (
                          <div
                            className={[
                              styles.bubbleHeader,
                              isClinic ? styles.bubbleHeaderRight : styles.bubbleHeaderLeft,
                            ].join(' ')}
                          >
                            <span
                              className={[
                                styles.senderLabel,
                                isClinic ? styles.senderLabelRight : styles.senderLabelLeft,
                              ].join(' ')}
                            >
                              {senderLabel}
                            </span>
                            <span className={styles.headerDot}>·</span>
                            <span className={styles.headerTime}>
                              {getConversationHeaderTime(message.sentAt)}
                            </span>
                          </div>
                        )}
                        <MessageBody text={message.body} />
                        {!!message.attachmentId && (
                          <button
                            type="button"
                            className={styles.attachmentButton}
                            onClick={() => onDownloadAttachment(message.attachmentId)}
                          >
                            Download attachment
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {canReply ? (
            <div className={styles.replyComposer}>
              <textarea
                id="care-orchestration-thread-reply-input"
                className={styles.replyInput}
                placeholder="Write a reply to the patient..."
                value={replyText}
                onChange={(event) => setReplyText(event.target.value)}
                rows={4}
              />
              <div className={styles.replyFooter}>
                <input
                  ref={replyAttachmentInputRef}
                  type="file"
                  className={styles.replyAttachmentInput}
                  onChange={(event) => setReplyAttachment(event.target.files?.[0] || null)}
                />
                <div className={styles.replyActions}>
                  <button
                    id="care-orchestration-thread-reply-send-button"
                    type="button"
                    className={styles.replyPrimaryButton}
                    disabled={!replyText.trim() || isSendingReply}
                    onClick={onSendReply}
                  >
                    {isSendingReply ? 'Sending...' : 'Send Reply'}
                  </button>
                  <button
                    id="care-orchestration-thread-reply-attach-button"
                    type="button"
                    className={styles.replySecondaryButton}
                    onClick={() => replyAttachmentInputRef.current?.click()}
                  >
                    Attach
                  </button>
                </div>
              </div>
              {!!replyAttachment && (
                <div className={styles.replyAttachmentName}>{replyAttachment.name}</div>
              )}
              <div className={styles.replyHint}>Reply goes to patient portal</div>
            </div>
          ) : (
            <Alert
              variant="warning"
              role="status"
              title="Claim this task to reply, escalate, or share the thread with a clinician."
            />
          )}
        </>
      )}
    </BriefRow>
  );
};

TaskConversation.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    patientId: PropTypes.number,
    threadId: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  canReply: PropTypes.bool,
  onReplySent: PropTypes.func,
};

export default TaskConversation;
